import { useNavigate, useSearch } from "@tanstack/react-router";
import {
  Announcement03,
  DotsVertical,
  FilePlus02,
  InfoCircle,
  MessageTextSquare02,
  // MinusCircle, // Commented out - Remove from Collab feature pending Samples API integration
  SearchMd,
  SlashCircle01,
  UserSquare,
  Users01,
} from "@untitledui/icons";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  type CandidateListItem,
  useCandidateRowsFetcher,
  useCandidates,
  useCandidatesDownload,
  useCandidatesMatchQuality,
} from "@/api/candidates";
import { usePagination } from "@/api/pagination";
import { useMerchantRecommendationsCount, useMerchantRecommendationsDownload } from "@/api/recommendations";
import { GroupedActionMenu } from "@/components/action-menu/grouped-action-menu";
import { SelectAllPagesButton } from "@/components/application/table/select-all-pages-button";
import { UnboxTable } from "@/components/application/table/unbox-table";
import type { UnboxTableColumn } from "@/components/application/table/unbox-table.types";
import { InviteToCollaborationModal } from "@/components/assurance/invite-to-collaboration-modal";
import { Button } from "@/components/base/buttons/button";
import { ButtonUtility } from "@/components/base/buttons/button-utility";
import { CloseButton } from "@/components/base/buttons/close-button";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { NETWORK_COLUMN_TOOLTIP, NetworkCellContent } from "@/components/creator-network/creator-network";
import { BulkTagButton } from "@/components/creator-tags/bulk-tag-button";
import { CreatorTagsCellContent } from "@/components/creator-tags/creator-tags-cell";
import { ExportCsvButton } from "@/components/ExportCsvButton";
import { FilterPill, FilterPills } from "@/components/filters/filter-pills";
import { CandidateFilterPanel } from "@/components/filters/panels";
import { SearchAndFilterActions } from "@/components/filters/search-and-filter-actions";
import { MessageCreatorsDialog } from "@/components/message-creator-dialogs";
import { PageLayout } from "@/components/page-layout";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { generateFilterSyncConfig, useAutoFilterUrlSync } from "@/hooks/use-auto-filter-sync";
import { useCache } from "@/hooks/use-cache";
import { useCrossPageSelection } from "@/hooks/use-cross-page-selection";
import { useCurrency } from "@/hooks/use-currency";
import { useClearableDebounce } from "@/hooks/use-debounce";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import { useSearchUrlSync } from "@/hooks/use-search-url-sync";
import { useSelectAllAcrossPages } from "@/hooks/use-select-all-across-pages";
import { useSort } from "@/hooks/use-sort";
import { useTrackedBlockCreators } from "@/hooks/use-tracked-block-creators";
import { useUrlFilterSync } from "@/hooks/use-url-filter-sync";
import { buildCandidateActionGroups } from "@/lib/creator-action-groups";
import { AddToCandidateListsModal } from "@/pages/candidate-lists/add-to-candidate-lists-modal";
import { CreateCandidateList } from "@/pages/candidates/create-candidate-list";
import { DeleteCreatorListModal } from "@/pages/creator-lists/delete-modal";
import { EditCreatorListModal } from "@/pages/creator-lists/edit-modal";
import { TagLibraryModal } from "@/pages/creator-tags/tag-library-modal";
import { CandidateDetails } from "@/pages/creators/candidate-details";
import { CandidateClassificationTabs, getClassificationFromTab } from "@/pages/explorers/candidate-classification-tabs";
import {
  GenderDemoCellContent,
  MainAgeDemoCellContent,
  MatchQualityCellContent,
} from "@/pages/explorers/columns/shared-cells";
import { downloadRecommendationsCSV } from "@/pages/explorers/recommendations-csv";
import { toRecommendationsFilters } from "@/pages/explorers/recommendations-filters";
import { RecommendationsTable } from "@/pages/explorers/recommendations-table";
import { FiltersProvider, useFilters } from "@/providers/filter-provider";
import { error as toastError, success as toastSuccess } from "@/providers/ToastProvider";
import type { candidateExplorerRoute } from "@/router";
import { getCreatorName } from "@/utils/creator-name";
import { downloadCSV } from "@/utils/download-csv";
import { compactNumberFormatter } from "@/utils/formatNumber";

const videoFormatter = new Intl.NumberFormat("en-US", {
  compactDisplay: "short",
  notation: "compact",
  maximumFractionDigits: 1,
});

const CLASSIFICATION_DESCRIPTIONS: Record<string, React.ReactNode> = {
  recommended_candidates: (
    <>
      <p className="font-semibold">Top Performer</p>
      <span>Must meet ≥3 of 4 criteria:</span>
      <ul className="list-disc pl-4">
        <li>Top 10% GMV (revenue)</li>
        <li>Top 20% Posts (posting frequency)</li>
        <li>Top 30% GMV/Post (revenue per post)</li>
        <li>Top 30% GPM (revenue per 1000 views)</li>
      </ul>
    </>
  ),
};

const CandidateExplorerContent = () => {
  const urlParams = useSearch({ strict: false }) as ReturnType<(typeof candidateExplorerRoute)["useSearch"]>;
  const { candidate_id: selectedCandidateId, search: urlSearch } = urlParams;
  const navigate = useNavigate() as ReturnType<(typeof candidateExplorerRoute)["useNavigate"]>;

  const [selectedTab, setSelectedTab] = useState("recommended_candidates");
  const [classificationTooltipOpen, setClassificationTooltipOpen] = useState(false);
  const isRecommendationsTab = selectedTab === "recommended_candidates";

  const { data: recommendationsCount } = useMerchantRecommendationsCount({
    isCreator: false,
    enabled: isRecommendationsTab,
  });

  const classificationFilter = getClassificationFromTab(selectedTab);
  const { form, apiFilters, reset, activeFields, hasActiveFilters: hasActiveFilterFields } = useFilters();

  // Automatically sync URL params to form based on active fields
  useAutoFilterUrlSync(form, urlParams, activeFields);

  // Generate field mapping and currentSearch dynamically
  const { fieldMapping, currentSearch } = useMemo(
    () => generateFilterSyncConfig(activeFields, urlParams),
    [activeFields, urlParams],
  );

  // Sync form changes back to URL
  useUrlFilterSync({
    form,
    fieldMapping,
    navigate,
    currentSearch,
  });

  const [search, setSearch] = useState(urlSearch || "");
  const { value: debouncedSearch, clear: clearSearch } = useClearableDebounce(search);
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [bulkInviteModalOpen, setBulkInviteModalOpen] = useState(false);
  const [rowInviteCandidateId, setRowInviteCandidateId] = useState<string>();
  const [rowMessageCandidateId, setRowMessageCandidateId] = useState<string>();
  const [selectedList, setSelectedList] = useState("__all");
  const [addToListsBulkOpen, setAddToListsBulkOpen] = useState(false);
  const [addToListsCandidateId, setAddToListsCandidateId] = useState<string>();
  const blockCreators = useTrackedBlockCreators();
  const creatorTagsEnabled = useFeatureFlag("F-creator-tags");
  const [tagLibraryOpen, setTagLibraryOpen] = useState(false);

  // Sync search to URL
  useSearchUrlSync({
    search,
    setSearch,
    navigate,
    currentSearch: { search: urlSearch },
  });

  const candidateListFilter = ["__all", "__create-new"].includes(selectedList) ? undefined : selectedList;
  const filters = useMemo(
    () => ({
      ...apiFilters,
      text: debouncedSearch,
      candidateList: candidateListFilter,
      classification: classificationFilter,
    }),
    [apiFilters, debouncedSearch, candidateListFilter, classificationFilter],
  );

  const recommendationsFilters = useMemo(
    () => toRecommendationsFilters(apiFilters, debouncedSearch),
    [apiFilters, debouncedSearch],
  );

  const currency = useCurrency();
  const gmvFormatter = useMemo(
    () =>
      new Intl.NumberFormat("en-US", {
        style: "currency",
        currency,
        notation: "compact",
        compactDisplay: "short",
      }),
    [currency],
  );

  const pagination = usePagination();
  const { sort, descriptor, setFromDescriptor } = useSort<
    "nickname" | "matchQualityScore" | "gmvAmount" | "ecVideoCount" | "followerCount"
  >("gmvAmount");

  const { data } = useCandidates({
    pagination: pagination.pagination,
    sort,
    filters,
  });

  // totalCount comes from the list response (COUNT(*) OVER ()) — no separate /count request.
  const total = useCache(data?.totalCount);

  // Lazily fetch Match Quality scores after the rows have painted (Phase 4).
  const matchQualityIds = useMemo(() => data?.candidates?.map((c) => c.tikTokCreatorOpenId) ?? [], [data]);
  const { data: matchQualityMap } = useCandidatesMatchQuality(matchQualityIds);

  const [filtersOpen, setFiltersOpen] = useState(false);

  const candidateColumns = useMemo<UnboxTableColumn<CandidateListItem>[]>(
    () => [
      {
        id: "nickname",
        label: "Creator",
        sortable: true,
        minWidth: "260px",
        render: (_value, item) => {
          const { nickname, handle } = getCreatorName(item.nickname, item.username);
          const displayName = nickname ?? handle;
          return (
            <div className="flex items-center gap-3">
              {item.avatar ? (
                <img
                  src={item.avatar}
                  alt={displayName ?? "Creator"}
                  className="h-10 w-10 rounded-full object-cover flex-shrink-0"
                />
              ) : (
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-50 flex-shrink-0">
                  <span className="text-xs font-medium text-primary">
                    {(nickname ?? item.username)?.charAt(0).toUpperCase() ?? "?"}
                  </span>
                </div>
              )}
              <div className="flex flex-col">
                <div className="text-xs font-medium text-gray-700">{displayName}</div>
                {nickname && handle && <div className="text-xs text-gray-500">{handle}</div>}
              </div>
            </div>
          );
        },
      },
      ...(creatorTagsEnabled
        ? [
            {
              id: "tags",
              label: "Tags",
              minWidth: "180px",
              render: (_value: unknown, item: CandidateListItem) => (
                <CreatorTagsCellContent
                  tiktokCreatorOpenId={item.tikTokCreatorOpenId}
                  tagIds={item.tagIds}
                  maxVisible={3}
                  onManageAllTags={() => setTagLibraryOpen(true)}
                />
              ),
            },
          ]
        : []),
      {
        id: "network",
        label: "Network",
        tooltip: NETWORK_COLUMN_TOOLTIP,
        minWidth: "130px",
        render: (_value, item) => <NetworkCellContent network={item.network} />,
      },
      {
        id: "matchQualityScore",
        label: "Match Quality",
        sortable: true,
        minWidth: "180px",
        render: (_value, item) => (
          <MatchQualityCellContent score={item.matchQualityScore ?? matchQualityMap?.[item.tikTokCreatorOpenId]} />
        ),
      },
      {
        id: "gmvAmount",
        label: "GMV (Last 30 Days)",
        sortable: true,
        minWidth: "140px",
        render: (_value, item) => (
          <span className="whitespace-nowrap text-xs text-primary font-medium">
            {item.gmvAmount != null ? gmvFormatter.format(item.gmvAmount) : "-"}
          </span>
        ),
      },
      {
        id: "topGender",
        label: "Gender Demographic",
        minWidth: "120px",
        render: (_value, item) => <GenderDemoCellContent genderDistribution={item.genderDistribution} />,
      },
      {
        id: "topAgeRange",
        label: "Main Age Demo",
        minWidth: "120px",
        render: (_value, item) => <MainAgeDemoCellContent topAgeRange={item.topAgeRange} />,
      },
      {
        id: "ecVideoCount",
        label: "Videos (Last 30 Days)",
        sortable: true,
        minWidth: "100px",
        render: (_value, item) => (
          <span className="whitespace-nowrap text-xs text-primary font-medium">
            {item.videoCount != null ? videoFormatter.format(item.videoCount) : "-"}
          </span>
        ),
      },
      {
        id: "followerCount",
        label: "Followers",
        sortable: true,
        minWidth: "120px",
        render: (_value, item) => (
          <span className="whitespace-nowrap text-xs text-primary font-medium">
            {item.followerCount != null ? videoFormatter.format(item.followerCount) : "-"}
          </span>
        ),
      },
      {
        id: "actions",
        label: "",
        minWidth: "60px",
        render: (_value, item) => (
          <div className="flex flex-row gap-1 items-center justify-end">
            <ButtonUtility
              size="xs"
              color="tertiary"
              tooltip="Invite to collaboration"
              icon={Announcement03}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                setRowInviteCandidateId(item.tikTokCreatorOpenId);
              }}
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="flex items-center justify-center w-8 h-8 hover:bg-secondary rounded transition-colors"
                  onClick={(e) => e.stopPropagation()}
                  aria-label="Candidate actions"
                >
                  <DotsVertical className="w-5 h-5 text-tertiary" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                <GroupedActionMenu
                  groups={buildCandidateActionGroups({
                    onInvite: () => setRowInviteCandidateId(item.tikTokCreatorOpenId),
                    onMessage: () => setRowMessageCandidateId(item.tikTokCreatorOpenId),
                    onAddToList: () => setAddToListsCandidateId(item.tikTokCreatorOpenId),
                  })}
                />
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ),
      },
    ],
    [matchQualityMap, gmvFormatter, creatorTagsEnabled],
  );

  type CandidateData = {
    id: string;
    creatorId: string;
    handle: string | null;
    thumbnail: string | null;
    nickname: string | null;
  };

  const {
    selectedKeys: selected,
    selectedData: selectedCandidatesData,
    isAllPagesSelected,
    selectedCount,
    clear: clearSelection,
    selectAllAcrossPages,
    createSelectionHandler,
  } = useCrossPageSelection<CandidateListItem, CandidateData>({
    getKey: useCallback((c: CandidateListItem) => c.tikTokCreatorOpenId, []),
    toData: useCallback(
      (c: CandidateListItem) => ({
        id: c.tikTokCreatorOpenId,
        creatorId: c.username,
        handle: c.nickname ?? c.username ?? null,
        thumbnail: c.avatar,
        nickname: c.nickname,
      }),
      [],
    ),
  });

  const handleSelectionChange = useMemo(() => createSelectionHandler(data?.candidates), [createSelectionHandler, data]);

  const fetchChunk = useCandidateRowsFetcher(filters);
  const selectAll = useSelectAllAcrossPages({
    fetchChunk,
    total: total ?? 0,
    loadedCount: data?.candidates?.length ?? 0,
    selection: { selectedCount, isAllPagesSelected, selectAllAcrossPages, clear: clearSelection },
    resetKey: JSON.stringify(filters),
  });
  const handleClearSelection = selectAll.clear;

  useEffect(() => {
    const subscription = form.watch(() => pagination.setPage(1));
    return subscription.unsubscribe;
  }, [form, pagination.setPage]);

  // Reset pagination when tab changes
  useEffect(() => {
    pagination.setPage(1);
  }, [selectedTab, selectedList, pagination.setPage]);

  useEffect(() => {
    setClassificationTooltipOpen(false);
  }, [selectedTab]);

  const handleRowClick = (candidateId: string) => {
    navigate({ search: (prev) => ({ ...prev, candidate_id: candidateId }) });
  };

  const handleCloseDetails = () => {
    navigate({ search: (prev) => ({ ...prev, candidate_id: undefined }) });
  };

  const { mutateAsync: fetchRecommendations, isPending: isDownloadingRecommendations } =
    useMerchantRecommendationsDownload();
  const downloadRecommendations = async () => {
    const rows = await fetchRecommendations({ isCreator: false, filters: recommendationsFilters });
    downloadRecommendationsCSV(rows, "recommended_creators", null);
  };

  const { mutateAsync, isPending: isDownloading } = useCandidatesDownload();

  const downloadTableData = async () => {
    if (!total) return;
    const rows = await mutateAsync({
      total,
      sort,
      filters,
    });
    const csvRows = rows.map((r) => ({
      ...r,
      topGender: r.topGender ? `${r.topGender.gender} (${r.topGender.percentage}%)` : "",
      topAgeRange: r.topAgeRange ? `${r.topAgeRange.range} (${r.topAgeRange.percentage}%)` : "",
    }));
    downloadCSV(
      csvRows,
      "candidate_explorer",
      [
        "tikTokCreatorOpenId",
        "nickname",
        "username",
        "matchQualityScore",
        "gmvAmount",
        "topGender",
        "topAgeRange",
        "videoCount",
        "followerCount",
      ],
      [
        "Creator ID",
        "Nickname",
        "Username",
        "Match Quality",
        "GMV",
        "Gender Demo",
        "Main Age Demo",
        "Videos",
        "Followers",
      ],
    );
  };

  const resetFilters = () => {
    setSearch("");
    clearSearch();
    reset();
  };

  const isInCandidateList = !["__all", "__create-new"].includes(selectedList);

  const trimmedSearch = search.trim();
  const searchPill = trimmedSearch ? (
    <FilterPill label="Search" value={trimmedSearch} onRemove={() => setSearch("")} />
  ) : undefined;

  const searchAndFilterActions = (
    <SearchAndFilterActions
      search={search}
      onSearchChange={setSearch}
      onToggleFilters={() => setFiltersOpen((prev) => !prev)}
    />
  );

  return (
    <PageLayout
      drawerOpen={filtersOpen}
      title="Find New Creators"
      icon={SearchMd}
      subtitle="Discover new creators who might be right for your brand"
      callToAction={
        selectedList !== "__create-new" && (
          <div className="flex flex-row items-center gap-2">
            {isInCandidateList && (
              <>
                <EditCreatorListModal id={selectedList} type="candidate" />
                <DeleteCreatorListModal id={selectedList} type="candidate" onDelete={() => setSelectedList("__all")} />
              </>
            )}
            <ExportCsvButton
              onClick={isRecommendationsTab ? downloadRecommendations : downloadTableData}
              isDownloading={isRecommendationsTab ? isDownloadingRecommendations : isDownloading}
            />
            {isRecommendationsTab && (
              <Button
                color="primary"
                iconLeading={UserSquare}
                isDisabled={!recommendationsCount?.hasUnactionedRecommendations}
                onClick={() =>
                  navigate({ to: "/merchant/$merchantId/review-recommendations", search: { type: "candidate" } })
                }
              >
                Review Matches
              </Button>
            )}
          </div>
        )
      }
    >
      <div className="flex flex-col gap-5 flex-1 min-h-0 min-w-0 w-full">
        {selected.length === 0 ? (
          <>
            <div className="flex flex-row items-end -mb-2">
              <CandidateClassificationTabs
                selected={selectedTab}
                setSelected={(tab) => {
                  setSelectedTab(tab);
                  setSelectedList("__all");
                  handleClearSelection();
                  resetFilters();
                }}
                selectedList={selectedList}
                setSelectedList={(list) => {
                  setSelectedList(list);
                  if (list !== "__all") {
                    setSelectedTab("");
                  }
                  handleClearSelection();
                }}
                showCustomLists
                showRecommendations
              />
            </div>

            {selectedList !== "__create-new" && !isRecommendationsTab && (
              <div className="flex flex-row items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-1.5 text-sm font-medium">
                  {total !== undefined ? `${compactNumberFormatter.format(total)} Candidates` : "Loading..."}

                  {selectedTab !== "__all" && CLASSIFICATION_DESCRIPTIONS[selectedTab] && (
                    <Tooltip
                      title={CLASSIFICATION_DESCRIPTIONS[selectedTab]}
                      isOpen={classificationTooltipOpen}
                      onOpenChange={setClassificationTooltipOpen}
                    >
                      <TooltipTrigger onPress={() => setClassificationTooltipOpen((prev) => !prev)}>
                        <InfoCircle className="w-4 h-4 text-tertiary" />
                      </TooltipTrigger>
                    </Tooltip>
                  )}
                </div>
                {searchAndFilterActions}
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-row justify-between items-center rounded-lg bg-brand-primary -my-2 p-2">
            <div className="flex flex-row items-center gap-2">
              <CloseButton onClick={handleClearSelection} />
              <span className="text-sm font-medium">{selectedCount.toLocaleString()} selected</span>
              <SelectAllPagesButton state={selectAll} />
            </div>
            <div className="flex flex-row gap-2 items-center ">
              <Button
                iconLeading={Announcement03}
                onClick={() => setBulkInviteModalOpen(true)}
                isDisabled={selectAll.isFetching}
              >
                Invite to Collab
              </Button>
              {/* Remove from Collab feature commented out - pending Samples API integration
                  <Button iconLeading={MinusCircle} onClick={() => setBulkRemoveModalOpen(true)}>
                    Remove from Collab
                  </Button>
                  */}
              <Button
                iconLeading={MessageTextSquare02}
                onClick={() => setMessageDialogOpen(true)}
                isDisabled={selectAll.isFetching}
              >
                Message
              </Button>
              <MessageCreatorsDialog
                recipients={Array.from(selectedCandidatesData.values())}
                open={messageDialogOpen}
                onOpenChange={setMessageDialogOpen}
                onSuccess={handleClearSelection}
              />
              {creatorTagsEnabled ? (
                <BulkTagButton
                  creators={[...selected].map((id) => ({
                    tiktokCreatorOpenId: id,
                    label: selectedCandidatesData.get(id)?.handle ?? undefined,
                  }))}
                  onApplied={handleClearSelection}
                  isDisabled={selectAll.isFetching}
                />
              ) : (
                <Button
                  iconLeading={FilePlus02}
                  onClick={() => setAddToListsBulkOpen(true)}
                  isDisabled={selectAll.isFetching}
                >
                  Add to list
                </Button>
              )}
              <Button
                iconLeading={SlashCircle01}
                isDisabled={selectAll.isFetching || blockCreators.isPending}
                onClick={async () => {
                  const openIds = [...selected];
                  if (openIds.length === 0) return;
                  try {
                    const blocked = await blockCreators.block(openIds);
                    if (blocked !== null) {
                      toastSuccess(
                        `${blocked} Creator${blocked === 1 ? "" : "s"} blocked. Unblock anytime from Operations/Blocked Creators.`,
                      );
                    }
                    handleClearSelection();
                  } catch {
                    toastError("Failed to block creators");
                  }
                }}
              >
                Block
              </Button>
            </div>
          </div>
        )}

        {!isRecommendationsTab && <FilterPills className="-my-2" extraPills={searchPill} />}
        {selectedList === "__create-new" ? (
          <CreateCandidateList onCreate={(id) => setSelectedList(id)} />
        ) : isRecommendationsTab ? (
          <>
            <RecommendationsTable
              isCreator={false}
              filters={recommendationsFilters}
              filterActions={searchAndFilterActions}
              filterSection={<FilterPills className="-my-2" extraPills={searchPill} />}
              onCreatorSelect={(candidateId) => {
                navigate({ search: (prev) => ({ ...prev, candidate_id: candidateId }) });
              }}
            />
            <CandidateDetails id={selectedCandidateId} onClose={handleCloseDetails} />
          </>
        ) : (
          <>
            <UnboxTable<CandidateListItem>
              aria-label="Candidate Explorer"
              className="max-h-[60vh]"
              columns={candidateColumns}
              rows={data?.candidates}
              rowId="tikTokCreatorOpenId"
              onClickRow={(row) => handleRowClick(row.tikTokCreatorOpenId)}
              selection={{ raw: true, selectedKeys: new Set(selected), onSelectionChange: handleSelectionChange }}
              sorting={{ descriptor, onSortChange: setFromDescriptor }}
              pagination={{
                page: pagination.page,
                totalPages: Math.ceil((total ?? 0) / pagination.pagination.limit),
                onPageChange: pagination.setPage,
              }}
              hasActiveFilters={hasActiveFilterFields || !!debouncedSearch}
              emptyState={{ icon: Users01, title: "No creators yet" }}
              filteredEmptyState={{
                icon: Users01,
                title: "No matches found right now",
                description: "Try browsing all creators to find creators right for your brand",
              }}
            />
            <CandidateDetails id={selectedCandidateId} onClose={handleCloseDetails} />
          </>
        )}
      </div>
      <CandidateFilterPanel open={filtersOpen} close={() => setFiltersOpen(false)} clearAll={() => resetFilters()} />

      {/* Invite to Collaboration Modal */}
      <InviteToCollaborationModal
        creators={Array.from(selectedCandidatesData.values()).map((c) => ({
          openId: c.id,
          username: c.creatorId,
          nickname: c.nickname,
          avatar: c.thumbnail,
        }))}
        open={bulkInviteModalOpen}
        onOpenChange={setBulkInviteModalOpen}
        onSuccess={handleClearSelection}
      />

      {/* Bulk Remove from Collaboration Modal - commented out - pending Samples API integration
        {(
          <BulkRemoveCreatorFromCollabModal
            creatorIds={Array.from(selected)}
            open={bulkRemoveModalOpen}
            onOpenChange={setBulkRemoveModalOpen}
            onSuccess={handleClearSelection}
          />
        )}
        */}

      {/* Row-level invite to collaboration */}
      {rowInviteCandidateId && (
        <InviteToCollaborationModal
          creators={
            data?.candidates
              .filter((c) => c.tikTokCreatorOpenId === rowInviteCandidateId)
              .map((c) => ({
                openId: c.tikTokCreatorOpenId,
                username: c.username,
                nickname: c.nickname,
                avatar: c.avatar,
              })) ?? []
          }
          open={!!rowInviteCandidateId}
          onOpenChange={(open) => !open && setRowInviteCandidateId(undefined)}
        />
      )}

      {/* Row-level message creator */}
      {rowMessageCandidateId && (
        <MessageCreatorsDialog
          recipients={
            data?.candidates
              .filter((c) => c.tikTokCreatorOpenId === rowMessageCandidateId)
              .map((c) => ({
                id: c.tikTokCreatorOpenId,
                creatorId: c.username,
                handle: c.nickname ?? c.username ?? null,
                thumbnail: c.avatar,
              })) ?? []
          }
          open={!!rowMessageCandidateId}
          onOpenChange={(open) => !open && setRowMessageCandidateId(undefined)}
          onSuccess={() => setRowMessageCandidateId(undefined)}
        />
      )}

      {/* Bulk Add to Lists Modal */}
      <AddToCandidateListsModal
        selectedCandidates={selected}
        onAddToList={handleClearSelection}
        open={addToListsBulkOpen}
        onOpenChange={setAddToListsBulkOpen}
      />

      {/* Row-level Add to Lists Modal */}
      <AddToCandidateListsModal
        selectedCandidates={addToListsCandidateId ? [addToListsCandidateId] : []}
        onAddToList={() => setAddToListsCandidateId(undefined)}
        open={addToListsCandidateId !== undefined}
        onOpenChange={(open) => {
          if (!open) setAddToListsCandidateId(undefined);
        }}
      />
      {creatorTagsEnabled && <TagLibraryModal open={tagLibraryOpen} onOpenChange={setTagLibraryOpen} />}
    </PageLayout>
  );
};

export const CANDIDATE_FILTER_FIELDS = [
  // Top Level Filters
  "minGmv",
  "maxGmv",
  "minRating",
  "maxRating",
  // "collaborationStatus",
  // Creator Overview Section
  "minFollowers",
  "maxFollowers",
  "topSellingCategories",
  "minTotalLikes",
  "maxTotalLikes",
  "minPostingFrequency",
  "maxPostingFrequency",
  "creatorLevel",
  "categoryPro",
  "creatorLanguage",
  // Creator Attributes Section
  "toneAndApproach",
  "contentFocus",
  "productionQuality",
  // Audience Section
  "maleAudiencePercent",
  "femaleAudiencePercent",
  "age",
  // Performance Section
  "minLiveGmv",
  "maxLiveGmv",
  "minVideoGmv",
  "maxVideoGmv",
  "includeTags",
  "excludeTags",
] as const;

export const CandidateExplorer = () => {
  const urlParams = useSearch({ strict: false }) as ReturnType<(typeof candidateExplorerRoute)["useSearch"]>;

  return (
    <FiltersProvider activeFields={[...CANDIDATE_FILTER_FIELDS]} initialValues={urlParams}>
      <CandidateExplorerContent />
    </FiltersProvider>
  );
};
